// src/utils/styleUtils.js
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import intervalToDuration from 'date-fns/intervalToDuration'; // <-- Import intervalToDuration

/**
 * Formats a Date object into a readable string.
 * @param {Date|null|undefined} date - The date object to format.
 * @param {Intl.DateTimeFormatOptions} [options] - Optional formatting options.
 * @returns {string} - The formatted date string or 'N/A'.
 */
export const formatDate = (date, options) => {
    // Check if date is a valid Date object
    if (!(date instanceof Date) || isNaN(date)) {
        return 'N/A';
    }
    // Default formatting options
    const defaultOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    try {
         // Use locale from browser settings (or specify like 'en-US')
        return date.toLocaleDateString(undefined, options || defaultOptions);
    } catch (e) {
        console.error("Error formatting date:", e, date);
        // Fallback to basic string if formatting fails
        return date.toDateString(); // Provides a readable fallback like "Sun Apr 27 2025"
    }
};

/**
 * Returns a CSS class name suffix based on task status.
 * @param {string|null|undefined} status - The task status string.
 * @returns {string} - The suffix for the CSS class (e.g., 'Completed', 'Approval').
 */
export const getStatusClassSuffix = (status) => {
    switch (status?.toLowerCase()) { // Use lowercase and optional chaining
        case 'completed':
            return 'Completed'; // Corresponds to .statusCompleted in CSS module
        case 'company approval': // Fall-through
        case 'customer approval':
            return 'Approval'; // Corresponds to .statusApproval
        case 'not started':
            return 'NotStarted'; // Corresponds to .statusNotStarted
        case 'cancelled': // Added previously
            return 'Cancelled'; // Corresponds to .statusCancelled
        // Add other statuses like 'In Progress' if needed
        // case 'in progress': return 'InProgress';
        default:
            return 'Default'; // Fallback for unknown or null/undefined status
    }
};

/**
 * Returns a CSS class name suffix based on task priority.
 * @param {string|null|undefined} priority - The priority string ('Low', 'Medium', 'High').
 * @returns {string} - The suffix for the CSS class (e.g., 'High', 'Medium', 'Low').
 */
export const getPriorityClassSuffix = (priority) => {
    switch (priority?.toLowerCase()) { // Use lowercase and optional chaining
        case 'high':
            return 'High'; // Corresponds to .priorityHigh
        case 'medium':
            return 'Medium'; // Corresponds to .priorityMedium
        case 'low':
            return 'Low'; // Corresponds to .priorityLow
        default:
            return 'Medium'; // Default to Medium if null, undefined, or unknown
    }
};

/**
 * Returns a CSS class name suffix based on campaign service type.
 * Used for styling calendar events.
 * @param {string|null|undefined} serviceType - The service type key (e.g., 'design', 'reelPro').
 * @returns {string} - The suffix for the CSS class (e.g., 'Design', 'ReelPro').
 */
export const getServiceClassSuffix = (serviceType) => {
    // Simple mapping, adjust keys/suffixes as needed to match your data and CSS
    switch (serviceType?.toLowerCase()) {
        case 'design': return 'Design';
        case 'reelcontentcreation': return 'ReelContent';
        case 'reelpro': return 'ReelPro';
        case 'video': return 'Video';
        case 'motiongraphics': return 'Motion';
        case 'cgi': return 'Cgi';
        case 'sponsorshipmanagement': return 'Sponsor';
        case 'printsjob': return 'Prints'; // Use a specific type for scheduled print jobs
        case 'meeting': return 'Meeting'; // Added example
        case 'other': return 'Other'; // Added example
        default: return 'Default'; // Fallback style
    }
};

// You can add other shared utility functions here as needed.

/**
 * Formats a Date object into a relative time string (e.g., "5 minutes ago").
 * @param {Date|null|undefined} date - The date object to format.
 * @returns {string} - The relative time string or an empty string.
 */
export const formatRelativeTime = (date) => {
    if (!(date instanceof Date) || isNaN(date)) return ''; // Return empty string for invalid dates
    try {
        return formatDistanceToNow(date, { addSuffix: true }); // Add "ago" or "in"
    } catch (e) {
        console.error("Error formatting relative time:", e, date);
        // Fallback to simple date format if relative formatting fails
        return formatDate(date, { dateStyle: 'short', timeStyle: 'short' });
    }
};





// --- NEW: Format Duration Between Dates ---
/**
 * Calculates and formats the duration between two dates into a readable string.
 * @param {Date | null | undefined} startDate - The starting date object.
 * @param {Date | null | undefined} endDate - The ending date object.
 * @returns {string} - Formatted duration (e.g., "1d 2h 30m") or empty string if invalid.
 */
export const formatDurationBetweenDates = (startDate, endDate) => {
    if (!(startDate instanceof Date) || isNaN(startDate) || !(endDate instanceof Date) || isNaN(endDate) || endDate < startDate) {
        return ''; // Return empty for invalid inputs
    }

    try {
        const duration = intervalToDuration({ start: startDate, end: endDate });

        // Build the formatted string piece by piece
        const parts = [];
        if (duration.years && duration.years > 0) parts.push(`${duration.years}y`);
        if (duration.months && duration.months > 0) parts.push(`${duration.months}mo`);
        if (duration.days && duration.days > 0) parts.push(`${duration.days}d`);
        if (duration.hours && duration.hours > 0) parts.push(`${duration.hours}h`);
        if (duration.minutes && duration.minutes > 0) parts.push(`${duration.minutes}m`);
        // Optionally add seconds if needed, but usually too granular
        // if (duration.seconds && duration.seconds > 0) parts.push(`${duration.seconds}s`);

        // If duration is very short (less than a minute), show '< 1m'
        if (parts.length === 0 && endDate.getTime() - startDate.getTime() > 0) {
            return '< 1m';
        }
        // If dates are identical
        if (parts.length === 0 && endDate.getTime() === startDate.getTime()) {
             return '0m'; // Or Instant
        }


        return parts.join(' '); // Join with spaces (e.g., "1d 4h 15m")
    } catch (e) {
        console.error("Error formatting duration:", e, startDate, endDate);
        return ''; // Fallback
    }
};


